// Unified browser extension API shim.
// Safari 16.4+ exposes `browser.*` (promise-based, Manifest V3).
// Chrome/Edge use `chrome.*` (callback-based).
// Selecting `browser` when available means the same code works in both.

import { isNativeApp } from './nativeApp';

declare const browser: typeof chrome | undefined;
type ExtensionApi = typeof chrome;

/** Unified extension API — use this instead of `chrome` or `browser` directly. */
export const ext: ExtensionApi =
  // eslint-disable-next-line no-undef
  typeof browser !== 'undefined'
    ? browser
    : typeof chrome !== 'undefined'
    ? chrome
    : ({} as ExtensionApi);

/** True when running inside an extension page (Chrome or Safari). */
export const isExtensionPage = (): boolean => {
  if (typeof window === 'undefined') return false;
  const proto = window.location.protocol;
  return proto === 'chrome-extension:' || proto === 'safari-web-extension:';
};

/** True when the extension runtime API is reachable (avoids null-ref in plain web contexts). */
export const hasExtensionRuntime = (): boolean =>
  Boolean((ext as any)?.runtime?.id);

/**
 * True when running inside the Safari extension. App Store builds must show no
 * purchase links or prices (App Review Guideline 3.1.3(b)) — gate upsell UI on this.
 */
export const isSafariExtension = (): boolean =>
  typeof window !== 'undefined' && window.location.protocol === 'safari-web-extension:';

/**
 * True on any Apple-distributed surface: the Safari extension OR the iOS/iPadOS
 * native app (WKWebView). Use this wherever Guideline 3.1.1 applies — no Stripe
 * checkout, no external purchase/pricing links on Apple platforms.
 */
export const isApplePlatform = (): boolean => isSafariExtension() || isNativeApp();
